import { get, isBoolean, isEmpty, isNil, isObject, set } from "lodash";
import {
  CONTACT_POINT_SYSTEM,
  PAYMENT_TYPES,
  RELATIONSHIP,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";
import { getAgeFromBirthDate } from "utils/helpers/miltisRelatedPersonChild";

export const MILTIS_OBSEQUE_PRODUCT = "OBSEQUE";

const MAJORITY_AGE = 18;

export const MEMBERSHIP_NATURE_OPTIONS = [
  { label: "Adhésion en ligne", value: "ONLINE" },
  { label: "Signature numérique", value: "DIGITAL" },
  { label: "Signature manuscrite", value: "HANDWRITTEN" },
];

export const LEGAL_PROTECTION_OPTIONS = [
  { label: "Non", value: "NONE" },
  { label: "Tutelle", value: "TUTELLE" },
  { label: "Curatelle (simple ou renforcée)", value: "CURATELLE" },
  { label: "Sauvegarde de justice", value: "SAUVEGARDE" },
  { label: "Habilitation familiale", value: "HABILITATION_FAMILIALE" },
  { label: "Habilitation du conjoint", value: "HABILITATION_CONJOINT" },
  { label: "Mesure d'accompagnement", value: "MESURE_ACCOMPAGNEMENT" },
  { label: "Mandat de protection future", value: "MANDAT_PROTECTION_FUTURE" },
];

export const BENEFICIARY_TYPE_OPTIONS = [
  { label: "Conjoint", value: RELATIONSHIP.SPOUSE },
  { label: "Autre", value: RELATIONSHIP.OTHER },
];

export const HOUSEHOLD_INCOME_OPTIONS = [
  { label: "Inférieurs à 1 000 €", value: "UNDER_1000" },
  { label: "1 000 € à 2 000 €", value: "FROM_1000_TO_2000" },
  { label: "2 000 € à 3 000 €", value: "FROM_2000_TO_3000" },
  { label: "Supérieurs à 3 000 €", value: "OVER_3000" },
];

export const PREVOYANCE_KNOWLEDGE_OPTIONS = [
  { label: "Vous n’avez aucune connaissance", value: "NONE" },
  { label: "Vous avez quelques connaissances", value: "SOME" },
  { label: "Vous connaissez très bien", value: "HIGH" },
];

export const OBSEQUES_DEBIT_DAYS = ["5", "10", "15"];

export const OBSEQUES_PAYMENT_TYPES = [
  PAYMENT_TYPES.DEBIT,
  PAYMENT_TYPES.CARD,
  PAYMENT_TYPES.TRANSFER,
  PAYMENT_TYPES.CHECK,
];

export const buildObsequesPaymentFields = (values) => {
  const fields = ["payment.type", "payment.frequency"];
  if (get(values, "payment.type") === PAYMENT_TYPES.DEBIT) {
    fields.push("payment.debitDay");
  }
  fields.push(
    "payment.payer.IBAN",
    "payment.payer.BIC",
    "payment.payer.owner.firstName",
    "payment.payer.owner.lastName",
  );
  return fields;
};

export const isMiltisObseques = () => true;

export const isSpouseIncluded = (values) => {
  const flag = get(values, "project.fields.spouseIncluded");
  return flag === true || flag === "true";
};

export const getObsequesChildrenCount = (values) =>
  Math.max(0, Number(get(values, "project.fields.childrenCount") || 0));

export const shouldShowObsequesChildren = (values) =>
  isSpouseIncluded(values) && getObsequesChildrenCount(values) > 0;

export const isObsequesFieldFilled = (value) => {
  if (isBoolean(value)) return value === true;
  if (isNil(value) || value === "") return false;
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (typeof value?.isValid === "function") return value.isValid();
  if (isObject(value)) return !isEmpty(value);
  return true;
};

export const isObsequesChildrenBlockDisabled = (values) =>
  !shouldShowObsequesChildren(values);

export const isOtherBeneficiary = (values) =>
  get(values, "beneficiary.relationship") === RELATIONSHIP.OTHER;

export const resolveObsequesFamilySituation = (contact = {}) =>
  get(contact, "familySituation") ||
  get(contact, "fields.familySituation") ||
  null;

export const hydrateObsequesFamilySituation = (contact = {}) => {
  const familySituation = resolveObsequesFamilySituation(contact);
  if (familySituation) {
    set(contact, "familySituation", familySituation);
  }
  return contact;
};

export const applyObsequesDefaults = (
  projectFields = {},
  relatedPersons = [],
) => {
  const hasSpouse = relatedPersons.some(
    (person) => get(person, "relationship") === RELATIONSHIP.SPOUSE,
  );
  const childrenCount = relatedPersons.filter(
    (person) => get(person, "relationship") === RELATIONSHIP.CHILD,
  ).length;
  let spouseIncluded = "false";
  if (
    projectFields.spouseIncluded === true ||
    projectFields.spouseIncluded === "true"
  ) {
    spouseIncluded = "true";
  } else if (
    projectFields.spouseIncluded === false ||
    projectFields.spouseIncluded === "false"
  ) {
    spouseIncluded = "false";
  } else if (hasSpouse) {
    spouseIncluded = "true";
  }

  return {
    ...projectFields,
    legalProtectionMeasure:
      get(projectFields, "legalProtectionMeasure") || "NONE",
    householdIncome: get(projectFields, "householdIncome") || "UNDER_1000",
    prevoyanceKnowledge: get(projectFields, "prevoyanceKnowledge") || "NONE",
    spouseIncluded,
    childrenCount:
      projectFields.childrenCount === 0 || projectFields.childrenCount
        ? projectFields.childrenCount
        : childrenCount,
  };
};

export const buildObsequesCollectedDisabled = (values = {}) => ({
  "project.contact.user.gender": true,
  "project.contact.user.lastname": true,
  "project.contact.user.firstname": true,
  "project.contact.user.birthDate": true,
  "project.contact.socialRegime": true,
  "project.contact.familySituation": Boolean(
    resolveObsequesFamilySituation(get(values, "project.contact")),
  ),
  "project.contact.profession": false,
  "project.contract.issuanceDate": true,
  [`telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`]: true,
  [`telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`]: true,
  [`telecoms.contact.${CONTACT_POINT_SYSTEM.EMAIL}.value`]: false,
  [`telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_ZIP_CODE}.value`]: true,
  [`telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`]: true,
});

const buildPartnerFields = (values) => {
  const fields = ["project.fields.spouseIncluded"];
  if (!isSpouseIncluded(values)) return fields;
  return [
    ...fields,
    "spouse.identity.user.firstname",
    "spouse.identity.user.lastname",
    "spouse.identity.user.birthDate",
    "spouse.identity.socioProfessionalCategory",
    "project.fields.childrenCount",
  ];
};

const buildChildrenFields = (values) => {
  if (!shouldShowObsequesChildren(values)) return [];
  return Array.from(
    { length: getObsequesChildrenCount(values) },
    (_, index) => `children.${index}.identity.user.birthDate`,
  );
};

const buildBeneficiaryFields = (values) => {
  const fields = ["beneficiary.relationship"];
  if (isOtherBeneficiary(values)) {
    fields.push("beneficiary.identity.fields.designation");
  }
  return fields;
};

export const buildObsequesAdditionalInfoFields = (values = {}) => {
  const fields = {
    [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTRACT]: [
      "project.contract.issuanceDate",
      "project.fields.membershipNature",
      "createdDate",
    ],
    [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ADHERENT]: [
      "project.contact.user.gender",
      "project.contact.user.lastname",
      "project.contact.user.firstname",
      "project.contact.user.birthDate",
      "project.contact.nationality",
      "project.contact.socialRegime",
      "project.contact.familySituation",
    ],
    [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY]: [
      "project.contact.profession",
    ],
    [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTACT]: [
      `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
      `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
      `telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`,
      `telecoms.contact.${CONTACT_POINT_SYSTEM.PHONE}.value`,
      `telecoms.contact.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
    ],
    [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER]:
      buildPartnerFields(values),
    [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.LEGAL_PROTECTION]: [
      "project.fields.legalProtectionMeasure",
    ],
    [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.BENEFICIARY]:
      buildBeneficiaryFields(values),
    [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION]: [
      "project.fields.householdIncome",
      "project.fields.monthlyCharges",
      "project.fields.futureExpenses",
      "project.fields.prevoyanceKnowledge",
      "project.fields.acceptProductStatuses",
      "project.fields.acceptAgreement",
      "project.fields.acceptFees",
      ...(get(values, "project.fields.phoneCanvassing") === true
        ? ["project.fields.phoneCanvassingConsent"]
        : []),
    ],
    [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PAYMENT]:
      buildObsequesPaymentFields(values),
  };

  if (shouldShowObsequesChildren(values)) {
    fields[SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN] =
      buildChildrenFields(values);
  }

  return fields;
};

export const isEmancipatedAdherent = (values) => {
  const flag = get(values, "project.fields.emancipe");
  return String(flag || "").toLowerCase() === "oui";
};

export const isNonEmancipatedMinorAdherent = (values) => {
  const birthDate = get(values, "project.contact.user.birthDate");
  if (!birthDate) return false;
  return (
    getAgeFromBirthDate(birthDate) < MAJORITY_AGE &&
    !isEmancipatedAdherent(values)
  );
};

export const buildObsequesProofFields = (values) => {
  const fields = {
    [SUBSCRIPTION_FORM_KEYS.PROOF.ID]: ["proof.cin"],
    [SUBSCRIPTION_FORM_KEYS.PROOF.BANK_ACCOUNT]: ["proof.paymentAccount"],
  };
  if (isEmancipatedAdherent(values)) {
    fields[SUBSCRIPTION_FORM_KEYS.PROOF.EMANCIPATION_CERTIFICATE] = [
      "proof.emancipationCertificate",
    ];
  }
  if (isNonEmancipatedMinorAdherent(values)) {
    fields[SUBSCRIPTION_FORM_KEYS.PROOF.LEGAL_REPRESENTATIVE_PROOF] = [
      "proof.legalRepresentativeProof",
    ];
  }
  return fields;
};

export const buildObsequesCoupleDisabled = (values) => {
  const couple = isSpouseIncluded(values);
  return {
    "spouse.identity.user.firstname": !couple,
    "spouse.identity.user.lastname": !couple,
    "spouse.identity.user.birthDate": !couple,
    "spouse.identity.fields.memberNumber": !couple,
    "spouse.identity.socioProfessionalCategory": !couple,
    "project.fields.childrenCount": !couple,
    "beneficiary.identity.fields.designation": !isOtherBeneficiary(values),
  };
};
